import './OrganizationCard.less';
import React, { useCallback, useEffect, useState } from 'react';
import { Card, Button, Row, Col, Spin, Tooltip, Select } from 'antd';
import {
  DeleteOutlined,
  ArrowRightOutlined,
  LoginOutlined,
  GlobalOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { OrgCredential, OrgStatus } from '../../interfaces/organization';
import OrganizationStatusIndicator from '../OrganizationStatusIndicator/OrganizationStatusIndicator';
import classNames from 'classnames';
import { OrgEnvironmentType } from '../../enums/organization';
import ToolsTreeModal from '../ToolsTreeModal/ToolsTreeModal';
import { useUserAccess } from '../../hooks/useUserAccess';
import {
  ToolConfig,
  ToolConfigDataAccess,
  ToolConfigDataTypeAccess,
} from '../../interfaces/toolConfig';
import { useAbility } from '../../hooks/useAbility';
import { Resource } from '../../enums/common';
import { makeApiCall } from '../../utils/makeApiCall';

interface OrganizationCardProps {
  envType: OrgEnvironmentType;
  organization: OrgCredential;
  toolConfigs: ToolConfig[];
  width?: number;
  isSelected: boolean;
  isLoading?: boolean;
  onConnect: (org: OrgCredential) => void;
  onDelete: (org: OrgCredential) => void;
  onSettings: (org: OrgCredential) => void;
  onChangeShareOption: (org: OrgCredential, newIsShared: boolean) => void;
  disabled?: boolean; // Disables all operations on this card
  orgHasOtherSharedCred?: boolean; // Another credential for this SF org is already shared
}

type StatusConfig = {
  className: string;
};
const STATUS_CONFIG: Record<OrgStatus | 'default', StatusConfig> = {
  [OrgStatus.ACTIVE]: {
    className: 'active',
  },
  [OrgStatus.EXPIRED]: {
    className: 'expired',
  },
  [OrgStatus.DELETED]: {
    className: 'invalid',
  },
  [OrgStatus.INVALID]: {
    className: 'invalid',
  },
  [OrgStatus.NEW]: {
    className: 'unknown',
  },
  [OrgStatus.UNKNOWN]: {
    className: 'unknown',
  },
  default: {
    className: 'unknown',
  },
} as const;

enum ShareOption {
  PRIVATE = 'private',
  SHARED = 'shared',
}

const OrganizationCard: React.FC<OrganizationCardProps> = ({
  envType,
  organization,
  toolConfigs,
  width = 400,
  isSelected,
  isLoading: isLoadingProp = false,
  onConnect,
  onDelete,
  onChangeShareOption,
  onSettings,
  disabled = false,
  orgHasOtherSharedCred = false,
}) => {
  const { currentAccount } = useUserAccess();
  const { currentAbility } = useAbility();
  const cirraUserId = currentAccount?.cirra_user_id || null;
  const cirraOrganizationId =
    (currentAccount as any)?.cirra_organization_id || null; // eslint-disable-line @typescript-eslint/no-explicit-any

  // A shared org is an org that was shared by a team administrator
  // for which the user has NOT yet authenticated
  // There is no copy of this org in the DB under the user's name
  const isSharedOrg =
    organization.cirra_user_id !== cirraUserId && organization.is_shared;

  // A cloned org is an org that was shared by a team administrator
  // and for which the user HAS authenticated
  // This means a copy was made in the DB under the user's name
  const isClonedOrg =
    organization.cirra_user_id === cirraUserId && !!organization.source_id;
  const isDataAccessFromParent = isSharedOrg || isClonedOrg;

  // Shared orgs are always displayed as expired and requires user to reauthorize
  const orgStatus = isSharedOrg ? OrgStatus.EXPIRED : organization.status;

  const { className: cardClassName } =
    STATUS_CONFIG[orgStatus] || STATUS_CONFIG.default;
  const [infoLines, setInfoLines] = useState<string[][]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const hasPermisionToUpdateOrg = currentAbility.can(
    'update',
    Resource.OAuthCredential,
    organization,
    'is_shared'
  );
  // Can't share if: no permission, is a clone, or another cred for this org is already shared
  const cannotShareDueToExisting =
    orgHasOtherSharedCred && !organization.is_shared;
  const canChangeShareOption =
    hasPermisionToUpdateOrg && !isClonedOrg && !cannotShareDueToExisting;

  const [dataAccessFromSharedOrg, setDataAccessFromSharedOrg] =
    useState<ToolConfigDataAccess | null>(null);

  useEffect(() => {
    const fetchDataAccess = async () => {
      if (!isDataAccessFromParent) {
        setDataAccessFromSharedOrg(null);
        return;
      }
      const fallbackDataAccess = {
        metadata: ToolConfigDataTypeAccess.None,
        personal_setup_data: ToolConfigDataTypeAccess.None,
        user_data: ToolConfigDataTypeAccess.None,
        business_data: ToolConfigDataTypeAccess.None,
      } as ToolConfigDataAccess;
      try {
        const sourceOrgId = isClonedOrg
          ? organization.source_id
          : organization.id;

        if (!sourceOrgId) {
          console.error(
            'Failed to detect ID of the source organization',
            organization
          );
          // Fallback to default read-only access if no source org ID
          setDataAccessFromSharedOrg(fallbackDataAccess);
          return;
        }

        // Fetch data access from the source organization credential
        const result = await makeApiCall({
          url: `/credentials/${sourceOrgId}/data-access`,
          method: 'GET',
        });

        const _dataAccess = result.data;
        setDataAccessFromSharedOrg(_dataAccess as ToolConfigDataAccess);
      } catch (error) {
        console.error('Failed to fetch data access for shared org:', error);
        setDataAccessFromSharedOrg(fallbackDataAccess);
      }
    };

    fetchDataAccess();
  }, [
    isDataAccessFromParent,
    organization.id,
    organization.source_id,
    isSharedOrg,
  ]);

  // For shared orgs, show the actual owner's email instead of "your team administrator"
  const credentialOwnerLabel =
    isSharedOrg && organization.cirra_user_id
      ? organization.cirra_user_id
      : 'your team administrator';

  let shareOptionTooltip = '';
  if (isClonedOrg) {
    shareOptionTooltip = `This organization was shared with you by ${credentialOwnerLabel}. You do not have permission to unshare it`;
  } else if (cannotShareDueToExisting) {
    shareOptionTooltip = `Another connection to this Salesforce org is already shared with the team. Only one shared connection per org is allowed.`;
  } else if (!canChangeShareOption) {
    if (organization.is_shared) {
      shareOptionTooltip = `This organization was shared with you by ${credentialOwnerLabel}. You do not have permission to unshare it`;
    } else {
      shareOptionTooltip = `Only team administrators have permission to share organizations`;
    }
  } else {
    // Default tooltip explaining what Shared/Private means
    shareOptionTooltip = `Private: Only you can use this organization. Shared: Team members can see and use this organization.`;
  }

  const canDeleteOrgCred = currentAbility.can(
    'delete',
    Resource.OAuthCredential,
    organization
  );
  const deleteTooltip = canDeleteOrgCred
    ? 'Delete Salesforce Org'
    : `This organization was shared with you by ${credentialOwnerLabel}. You do not have permission to delete it`;

  // Combine prop loading state with local state
  const isCardLoading = isLoadingProp || isLoading || !toolConfigs?.length;

  const orgUrl = organization.hostname || organization.instance_url;

  // Extract My Domain from hostname or instance_url
  const myDomain = (() => {
    const url = organization.hostname || organization.instance_url;
    if (!url) return '-';
    // Remove protocol if present and extract hostname
    try {
      const hostname = url.startsWith('http')
        ? new URL(url).hostname
        : url.split('/')[0];
      // Remove common Salesforce suffixes to get just the My Domain part
      // Order matters: more specific patterns (sandbox, scratch, develop) must come before the general one
      return hostname
        .replace(/\.sandbox\.my\.salesforce\.com$/, '')
        .replace(/\.scratch\.my\.salesforce\.com$/, '')
        .replace(/\.develop\.my\.salesforce\.com$/, '')
        .replace(/\.my\.salesforce\.com$/, '');
    } catch {
      return url;
    }
  })();

  useEffect(() => {
    // Always show all fields for consistent card height
    // For shared orgs not yet authorized, User Id may not be available
    setInfoLines([
      ['Domain', myDomain],
      [
        'User Id',
        isSharedOrg
          ? 'Not yet authorized'
          : organization.user_id || 'Not yet authorized',
      ],
    ]);
  }, [organization.user_id, myDomain, isSharedOrg]);

  const handleChangeShareOption = useCallback(
    async (shareOption: ShareOption) => {
      const isShared = shareOption === ShareOption.SHARED;
      onChangeShareOption(organization, isShared);
    },
    [organization, onChangeShareOption]
  );

  return (
    <Card
      className={classNames([
        'organization-card',
        cardClassName,
        isSelected && 'selected',
        isSharedOrg && 'shared-pending',
      ])}
      style={{ width }}
    >
      {/* Header Row */}
      <Row
        align="middle"
        justify="space-between"
        className="org-card-header-row"
      >
        <Col flex="auto" className="org-card-header-left" span={19}>
          <span className="org-card-username">
            {isSharedOrg
              ? 'Shared org - select to authorize'
              : organization.username || '-'}
          </span>
          <OrganizationStatusIndicator
            cred={{ ...organization, status: orgStatus }}
            tooltipOverride={
              isSharedOrg ? 'Authorize connection to shared org' : undefined
            }
            classNameOverride={isSharedOrg ? 'pending' : undefined}
          />
        </Col>

        <Col className="org-card-header-right" span={5}>
          {isCardLoading ? (
            <Spin />
          ) : (
            <>
              <ToolsTreeModal
                envType={envType}
                title={`Tool settings for org ${organization.username}`}
                cirraUserId={cirraUserId}
                cirraOrganizationId={cirraOrganizationId}
                sfOrgId={organization.id}
                resetLabel={`Reset to default settings for ${envType} orgs`}
                toolConfigs={isDataAccessFromParent ? [] : toolConfigs}
                readOnlyDataAccess={
                  isDataAccessFromParent && dataAccessFromSharedOrg
                    ? dataAccessFromSharedOrg
                    : undefined
                }
                readOnlyDataAccessMessage={
                  isDataAccessFromParent
                    ? 'Settings are read only because this is a shared organization'
                    : undefined
                }
              />
              <Tooltip title={deleteTooltip}>
                <Button
                  type="text"
                  icon={<DeleteOutlined />}
                  className="org-card-btn org-card-delete-btn"
                  disabled={disabled || isCardLoading || !canDeleteOrgCred}
                  onClick={async () => {
                    setIsLoading(true);
                    try {
                      await onDelete(organization);
                    } catch (error) {
                      console.error('Failed to delete organization:', error);
                    } finally {
                      setIsLoading(false);
                    }
                  }}
                />
              </Tooltip>
              <Tooltip
                title={
                  !orgUrl
                    ? 'Organization URL is not available'
                    : 'Open Salesforce Org'
                }
              >
                <Button
                  type="text"
                  icon={<LoginOutlined />}
                  className="org-card-btn org-card-open-btn"
                  disabled={!orgUrl}
                  onClick={async () => {
                    if (!orgUrl) {
                      return;
                    }

                    const baseUrl = orgUrl.startsWith('http')
                      ? orgUrl
                      : `https://${orgUrl}`;
                    try {
                      // login_hint should be at root level, with startURL for the destination
                      const url = new URL(baseUrl);
                      url.pathname = '/';
                      url.searchParams.set(
                        'startURL',
                        '/lightning/setup/SetupOneHome/home'
                      );
                      if (organization.username) {
                        url.searchParams.set(
                          'login_hint',
                          organization.username
                        );
                      }
                      window.open(url.toString(), '_blank');
                    } catch (error) {
                      console.error('Failed to open organization URL:', error);
                      // Fallback to simple URL concatenation
                      const params = new URLSearchParams();
                      params.set(
                        'startURL',
                        '/lightning/setup/SetupOneHome/home'
                      );
                      if (organization.username) {
                        params.set('login_hint', organization.username);
                      }
                      window.open(`${baseUrl}/?${params.toString()}`, '_blank');
                    }
                  }}
                />
              </Tooltip>
            </>
          )}
        </Col>
      </Row>
      {/* Info Fields */}
      <div className="org-card-info-fields">
        <Row className="org-card-info-row">
          <Col span={5} className="org-card-info-label">
            Org Id:
          </Col>
          <Col span={12} className="org-card-info-value">
            {organization.organization_id}
          </Col>

          {organization.cirra_organization_id && (
            <Col span={7} className="org-card-share-select-container">
              {isCardLoading ? (
                <Spin className="org-card-share-select-spinner" size="small" />
              ) : (
                <Tooltip title={shareOptionTooltip}>
                  <Select
                    className="org-card-share-select"
                    size="small"
                    value={
                      organization.is_shared || isSharedOrg || isClonedOrg
                        ? ShareOption.SHARED
                        : ShareOption.PRIVATE
                    }
                    disabled={!canChangeShareOption}
                    onChange={handleChangeShareOption}
                    options={[
                      {
                        value: ShareOption.PRIVATE,
                        label: (
                          <span>
                            <UserOutlined /> Private
                          </span>
                        ),
                      },
                      {
                        value: ShareOption.SHARED,
                        label: (
                          <span>
                            <GlobalOutlined /> Shared
                          </span>
                        ),
                      },
                    ]}
                  />
                </Tooltip>
              )}
            </Col>
          )}
        </Row>
        {infoLines.map(([field, value]) => (
          <Row key={field} className="org-card-info-row">
            <Col span={5} className="org-card-info-label">
              {field}:
            </Col>
            <Col span={19} className="org-card-info-value">
              {value}
            </Col>
          </Row>
        ))}
      </div>
      {/* Connect Link */}
      <div className="org-card-connect-container">
        <Button
          type="link"
          className="org-card-connect-btn"
          disabled={disabled || isCardLoading}
          onClick={async () => {
            setIsLoading(true);
            try {
              await onConnect(organization);
            } catch (error) {
              console.error('Failed to connect to organization:', error);
            } finally {
              setIsLoading(false);
            }
          }}
        >
          {isCardLoading ? (
            <Spin size="small" />
          ) : (
            <span>
              {isSelected ? 'Reauthorize' : 'Select'} &nbsp;{' '}
              <ArrowRightOutlined />
            </span>
          )}
        </Button>
      </div>
    </Card>
  );
};

export default OrganizationCard;
